import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
  // FE-06: a <details> summary takes focus too, so Tab from the last one wraps.
  'details > summary:first-of-type',
].join(',');

// FE-06: what a closed <details> hides cannot take focus (its own summary can),
// so it must not count as the first or last control of the trap.
function folded(element) {
  for (let details = element.parentElement?.closest('details:not([open])'); details; details = details.parentElement?.closest('details:not([open])')) {
    const summary = [...details.children].find(child => child.tagName === 'SUMMARY');
    if (!summary?.contains(element)) return true;
  }
  return false;
}

function focusableElements(container) {
  if (!container?.querySelectorAll) return [];
  return [...container.querySelectorAll(FOCUSABLE_SELECTOR)].filter(element => (
    !element.hidden
    && element.getAttribute('aria-hidden') !== 'true'
    && !element.closest('[inert]')
    && !folded(element)
  ));
}

function focusElement(element) {
  if (!element?.focus) return;
  try { element.focus({ preventScroll:true }); } catch { element.focus(); }
}

export function useDialogFocus({ open = true, onClose, containerRef, initialFocusRef } = {}) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;
    const container = containerRef?.current;
    if (!container) return undefined;
    const previous = document.activeElement;
    const controls = focusableElements(container);
    focusElement(initialFocusRef?.current || controls[0] || container);

    const isTopmostDialog = () => {
      const dialogs = [...document.querySelectorAll('[role="dialog"][aria-modal="true"]')];
      return !dialogs.length || dialogs[dialogs.length - 1] === container;
    };
    const onKeyDown = event => {
      if (!isTopmostDialog()) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        closeRef.current?.();
        return;
      }
      if (event.key !== 'Tab') return;
      const nextControls = focusableElements(container);
      if (!nextControls.length) {
        event.preventDefault();
        focusElement(container);
        return;
      }
      const first = nextControls[0];
      const last = nextControls[nextControls.length - 1];
      if (!container.contains(document.activeElement)) {
        event.preventDefault();
        focusElement(event.shiftKey ? last : first);
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        focusElement(last);
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        focusElement(first);
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      if (previous?.isConnected) focusElement(previous);
    };
  }, [containerRef, initialFocusRef, open]);
}

